import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Box, Typography as T, useMediaQuery } from "@mui/material";

import { Grid } from "components/atoms";
import {
  InputNumberBox,
  InputSelect,
  PageWrapper,
  Toggle,
} from "components/molecules";
import { defaultFormValues } from "helpers/constants";
import { locationsMatchingSession } from "helpers/functions";
import { FormDataModel, SjdBookingInlineData } from "helpers/global-types";
import { stepHistoryHelper } from "helpers/navigation";
import { useResetForm, useTokenCart, useTypedTheme } from "hooks";
import { useStoreActions, useStoreState } from "store/store";
import { palette } from "theme/palette";

import { NoVehiclesError } from "./components/NoVehiclesError";
import RadioButton from "./components/RadioButton";

const Step1 = () => {
  // styles
  const {
    theme: {
      breakpoints: { down },
    },
  } = useTypedTheme();
  const isTablet = useMediaQuery(down("md"));
  // actions
  const getLocationsThunk = useStoreActions((a) => a.getLocationsThunk);
  const getVehiclesThunk = useStoreActions((a) => a.getVehiclesThunk);
  const updateCartThunk = useStoreActions((a) => a.updateCartThunk);
  const setSelectedCar = useStoreActions((a) => a.setSelectedCar);
  const setLoadingMessage = useStoreActions((a) => a.setLoadingMessage);
  const setIsCustomDestination = useStoreActions(
    (a) => a.setIsCustomDestination
  );
  const setIsDestinationAirport = useStoreActions(
    (a) => a.setIsDestinationAirport
  );
  const getInlineScriptVars = useStoreActions((a) => a.getInlineScriptVars);
  // state
  const cart = useStoreState((s) => s.cart);
  const locations = useStoreState((s) => s.locations);
  const isCustomDestination = useStoreState((s) => s.isCustomDestination);
  const selectedCar = useStoreState((s) => s.selectedCar);

  const navigate = useNavigate();
  useTokenCart();

  const location = useLocation();
  const { handleSubmit, setValue, watch } = useFormContext<FormDataModel>();
  const [noVehiclesError, setNoVehiclesError] = useState(false);
  const locationIsAirport = watch("pickupLocation")?.is_airport === "1";
  const destinationIsAirport = watch("destinationLocation")?.is_airport === "1";
  const formLocation = watch("pickupLocation");
  const formDestination = watch("destinationLocation");
  const formIsRoundTripReservation = watch("roundTripReservation");
  const formSelectedCar = watch("selectedCar");
  const defaults = defaultFormValues[1];

  useResetForm({ defaults, cart });

  const onSubmit = async (data: FormDataModel) => {
    if (isCustomDestination) {
      return;
    }

    // Dynamic loading message with the actual route the user picked.
    // Overrides PageWrapper's static "Finding rides for your route…"
    // fallback so the shimmer reads like context-aware narration:
    // "Looking up vehicles from SJD Airport to Marquis Los Cabos…".
    // Falls back to the generic phrasing if either name is missing —
    // shouldn't happen since both are required to reach this point.
    const pickupName = data.pickupLocation?.name;
    const destName = data.destinationLocation?.name;
    if (pickupName && destName) {
      setLoadingMessage(
        `Looking up vehicles from ${pickupName} to ${destName}…`
      );
    }

    const isLocationsMatchingSession = locationsMatchingSession({
      formDestination,
      formLocation,
      sessionLocation: cart?.location,
      sessionDestination: cart?.destination,
      sessionIsRoundTrip: !!cart?.roundtrip,
      formIsRoundTrip: !!formIsRoundTripReservation,
    });

    if (
      !isLocationsMatchingSession ||
      (formIsRoundTripReservation && isCustomDestination)
    ) {
      await updateCartThunk({
        is_custom_location: Boolean(isCustomDestination),
        passengers: Number(data.numberOfPassengers),
        roundtrip: Number(data.roundTripReservation),
        location_id: Number(data.pickupLocation?.location_id),
        destination_id: Number(data.destinationLocation?.location_id),
        ip_address: (window as SjdBookingInlineData).sjd_booking_visitor_ip,
      });
    } else {
      await updateCartThunk({
        passengers: Number(data.numberOfPassengers),
        roundtrip: Number(data.roundTripReservation),
        ip_address: (window as SjdBookingInlineData).sjd_booking_visitor_ip,
      });
    }

    if (!isCustomDestination) {
      if (!data || !data?.pickupLocation || !data?.destinationLocation) return;
      const vehiclesResponse = await getVehiclesThunk({
        pickup_location: data?.pickupLocation.location_id,
        destination: data?.destinationLocation.location_id,
      });

      if (vehiclesResponse?.length > 0) {
        return stepHistoryHelper.next(
          navigate,
          false,
          location.pathname,
          selectedCar?.isShuttle
        );
      } else {
        setNoVehiclesError(true);
      }
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsCustomDestination(e.target.value === "isCustomDestination");
    updateCartThunk({
      is_custom_location: e.target.value === "isCustomDestination",
      custom_location:
        e.target.value === "isCustomDestination" ? "" : undefined,
      ip_address: (window as SjdBookingInlineData).sjd_booking_visitor_ip,
    });
    if (selectedCar) {
      setSelectedCar(null);
    }
  };

  useEffect(() => {
    getLocationsThunk().then(() => {
      if (!cart?.location) {
        const inlineScriptVars = getInlineScriptVars();

        setValue("pickupLocation", {
          location_id: inlineScriptVars?.default_location ?? "958",
          name: "SJD Airport",
          type: "Airports",
          is_airport: "1",
          is_areas: "0",
        });
      }
    });
  }, []);

  // Reactive cart→form sync.
  //
  // useResetForm() above is one-shot — it runs in a useEffect with deps
  // [defaults], which never change after mount. That works when step-1
  // mounts with a cart already populated (e.g. back-nav from step 2+),
  // but NOT when cart gets populated AFTER mount:
  //
  //   1. User lands on `/book-now?pickup=958&destination=929`.
  //   2. Step-1 mounts with an empty cart → useResetForm fills `defaults`.
  //   3. useTokenCart fires updateCartThunk in the same tick.
  //   4. The thunk's response writes `cart.location` + `cart.destination`
  //      into the store ~300ms later — but the form fields don't move
  //      because nothing in this component re-syncs them.
  //
  // Previously a `location.reload()` was bolted on at the end of the
  // useTokenCart query-param path as a workaround — full remount,
  // useResetForm re-fires with the populated cart, fields land. The
  // cost was an extra page load (loading screen → flash → reload →
  // loading screen → form). The effects below fix the underlying race:
  // each one watches a specific cart slice and setValue's it onto the
  // form whenever the slice resolves. Targeted instead of "reset the
  // whole form on cart change" so in-flight user input on adjacent
  // fields isn't clobbered if a background sync ever updates one of
  // these slices.
  useEffect(() => {
    if (!cart?.location?.location_id) return;
    setValue("pickupLocation", cart.location);
  }, [cart?.location?.location_id]);

  useEffect(() => {
    if (!cart?.destination?.location_id) return;
    setValue("destinationLocation", cart.destination);
  }, [cart?.destination?.location_id]);

  useEffect(() => {
    if (cart?.passengers === undefined || cart?.passengers === null) return;
    setValue("numberOfPassengers", Number(cart.passengers));
  }, [cart?.passengers]);

  useEffect(() => {
    if (cart?.roundtrip === undefined || cart?.roundtrip === null) return;
    setValue("roundTripReservation", Boolean(cart.roundtrip));
  }, [cart?.roundtrip]);

  useEffect(() => {
    if (destinationIsAirport) {
      setIsDestinationAirport(true);
      setValue("roundTripReservation", false);
    }
  }, [
    locationIsAirport,
    isCustomDestination,
    formIsRoundTripReservation,
    destinationIsAirport,
    setIsDestinationAirport,
  ]);

  return (
    <PageWrapper step="step-1" onSubmit={onSubmit} handleSubmit={handleSubmit}>
      <T variant="h1">Service Selection</T>
      <Box display="flex" flexDirection="row">
        <RadioButton
          value="destination"
          checked={!isCustomDestination}
          labelText="Airport or Point to Point Transfer"
          handleChange={handleChange}
        />
        <Box
          sx={{
            alignSelf: "center",
            marginRight: "16px",
            height: "85%",
            width: 0,
            border: `1px solid ${palette.grey}`,
          }}
        />
        <RadioButton
          value="isCustomDestination"
          checked={isCustomDestination}
          labelText="Airbnb/Vrbo
          /Villa Transfer"
          handleChange={handleChange}
        />
      </Box>
      {!isCustomDestination ? (
        <>
          <Grid columnNumber={2}>
            <InputSelect
              placeholder="Pickup Location"
              name="pickupLocation"
              autocompleteData={locations}
              labelText="Pickup Location"
              required
              additionalOnChange={() => setNoVehiclesError(false)}
            />
            <InputSelect
              placeholder="Type your destination…"
              name="destinationLocation"
              autocompleteData={locations}
              labelText="Destination"
              required
              additionalOnChange={() => setNoVehiclesError(false)}
            />
          </Grid>
          <Box
            sx={{
              marginTop: 2,
              paddingTop: 3,
              borderTop: `1px solid ${palette.grey}`,
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                columnGap: 3,
                rowGap: 2,
                alignItems: "start",
              }}
            >
              {!destinationIsAirport ? (
                <Toggle
                  labelText="Make this a Round-Trip Reservation"
                  name="roundTripReservation"
                  isRoundCheck
                  labelVariant="inputTitle"
                >
                  <T variant="secondaryText" color={palette.lightGrey}>
                    Save yourself a travel headache! We will pick you up from
                    the destination we dropped you off at and take you to the
                    airport 3 hours before your flight&apos;s departure!
                  </T>
                </Toggle>
              ) : (
                <Box />
              )}
              <Box
                sx={{
                  justifySelf: "stretch",
                  marginLeft: isTablet ? "32px" : 0,
                }}
              >
                <InputNumberBox
                  required
                  labelText="Number of passengers"
                  name="numberOfPassengers"
                  labelErrorText="The field cannot be empty"
                  selectedCar={formSelectedCar}
                  labelVariant="inputTitle"
                />
              </Box>
            </Box>
          </Box>
        </>
      ) : (
        <Box>
          <T>
            For custom quotes please use this form: {""}
            <a
              href="https://transportation.sjdtaxi.com/cabo-airport-transportation-quote"
              target="_blank"
              rel="noopener noreferrer"
            >
              Transportation Quote
            </a>
          </T>
        </Box>
      )}
      <NoVehiclesError noVehiclesError={noVehiclesError} />
      <Outlet />
    </PageWrapper>
  );
};

export default Step1;
